import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import showToast from '../../utils/toastHelper';
import { useUser } from '../../contexts/UserContext';

const Settings = () => {
  const { hasRole } = useUser();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serviceCharge, setServiceCharge] = useState('');
  const [organization, setOrganization] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [annualPackages, setAnnualPackages] = useState([]);
  const [subscribing, setSubscribing] = useState(false);
  const isSuperAdmin = hasRole('super_admin');
  const isBillingPage = location.pathname === '/admin/billing';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSettings();
    } else {
      Promise.all([fetchOrganization(), fetchAnnualPackages()]).finally(() => setLoading(false));
    }
  }, [isSuperAdmin]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data.data);
      setServiceCharge(response.data.data.service_charge?.value || 300);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = userResponse.data.data;
      
      if (user.organizationId) {
        const orgResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/organizations/${user.organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrganization(orgResponse.data.data);
        if (orgResponse.data.data.logoUrl) {
          setLogoPreview(orgResponse.data.data.logoUrl);
        }

        // Fetch subscription for all org admins so Billing can show current status
        try {
          const subResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/subscriptions/organization/${user.organizationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSubscription(subResponse.data.data);
        } catch (subErr) {
          console.error('Failed to fetch subscription:', subErr);
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchAnnualPackages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/pricing-packages?type=annual`);
      const annual = response.data.data || [];
      setAnnualPackages(annual);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleSubscribe = async (packageId) => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscriptions/subscribe`,
        { packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data.paymentLink) {
        localStorage.setItem('pendingSubscriptionPayment', 'true');
        window.location.href = response.data.data.paymentLink;
      } else {
        showToast('Failed to initiate subscription payment', 'error');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      showToast(error.response?.data?.message || 'Failed to subscribe', 'error');
    } finally {
      setSubscribing(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showToast('File size must be less than 2MB', 'error');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      showToast('Please select a logo to upload', 'error');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', logoFile);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/${organization.id}/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      showToast('Logo uploaded successfully', 'success');
      setLogoFile(null);
      fetchOrganization();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      showToast('Failed to upload logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!serviceCharge || isNaN(serviceCharge) || parseFloat(serviceCharge) < 0) {
      showToast('Please enter a valid service charge amount', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/settings/service_charge`,
        { value: parseFloat(serviceCharge) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Service charge updated successfully', 'success');
      fetchSettings();
    } catch (error) {
      console.error('Failed to update settings:', error);
      showToast('Failed to update settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>
          {isSuperAdmin ? 'System Settings' : (isBillingPage ? 'Billing & Subscription' : 'Organization Settings')}
        </h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          {isSuperAdmin 
            ? 'Configure system-wide settings and preferences'
            : (isBillingPage ? 'Manage annual subscription payments for your organization' : 'Manage your organization profile')
          }
        </p>
      </div>

      {/* Settings Card */}
      <div className="row">
        <div className={isSuperAdmin ? 'col-lg-8' : 'col-12'}>
          {/* Organization Profile Card - FOR ADMINS ONLY */}
          {!isSuperAdmin && !organization && (
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              No organization found. Please contact support.
            </div>
          )}
          {!isSuperAdmin && organization && !isBillingPage && (
            <div className="card mb-4">
              <div className="card-header bg-white">
                <h5 className="mb-0" style={{ fontWeight: '600' }}>
                  <i className="bi bi-building me-2" style={{ color: '#667eea' }}></i>
                  Organization Profile
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '600' }}>
                    Organization Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={organization.name}
                    disabled
                    style={{ backgroundColor: '#f9fafb' }}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label d-block" style={{ fontWeight: '600' }}>
                    Organization Logo
                  </label>
                  <div
                    className="border rounded p-4 text-center bg-light position-relative"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file) handleLogoChange({ target: { files: [file] } });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {logoPreview ? (
                      <div>
                        <img
                          src={logoPreview}
                          alt="Organization logo"
                          style={{
                            maxWidth: '200px',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            border: '3px solid #667eea'
                          }}
                        />
                        <br />
                        <div className="mt-3">
                          {logoFile && (
                            <button
                              type="button"
                              onClick={handleLogoUpload}
                              className="btn btn-primary me-2"
                              disabled={uploadingLogo}
                            >
                              {uploadingLogo ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-upload me-2"></i>
                                  Upload Logo
                                </>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setLogoFile(null);
                              if (!organization.logoUrl) {
                                setLogoPreview(null);
                              } else {
                                setLogoPreview(organization.logoUrl);
                              }
                            }}
                            className="btn btn-outline-danger btn-sm"
                            disabled={uploadingLogo}
                          >
                            {logoFile ? 'Cancel' : 'Remove Logo'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-center text-muted">
                          <i className="bi bi-cloud-upload" style={{ fontSize: '3rem' }}></i>
                          <p className="mb-2 text-muted">Click anywhere or drag a file here to upload</p>
                          <small className="text-muted">Accepted formats: JPG, PNG, WEBP, SVG (max 2MB)</small>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          disabled={uploadingLogo}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Status Card - FOR ORG ADMINS */}
          {!isSuperAdmin && organization && (
            <div className="card mb-4">
              <div className="card-header bg-white">
                <h5 className="mb-0" style={{ fontWeight: '600' }}>
                  <i className="bi bi-calendar-check me-2" style={{ color: '#10b981' }}></i>
                  Billing & Subscription
                </h5>
              </div>
              <div className="card-body">
                {subscription ? (
                  <div>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: '1.5rem' }}></i>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '1rem', color: '#15803d' }}>Active Subscription</div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{subscription.pricingPackage?.name} Plan</div>
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-sm-4">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f9fafb' }}>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>Start Date</div>
                          <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{new Date(subscription.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f9fafb' }}>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>Expiry Date</div>
                          <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{new Date(subscription.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f0fdf4' }}>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>Days Remaining</div>
                          <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#10b981' }}>
                            {Math.max(0, Math.ceil((new Date(subscription.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="alert mb-0" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      Your annual subscription allows unlimited election creation without per-election payment.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-3">
                      <i className="bi bi-calendar-x" style={{ fontSize: '2.5rem', color: '#d1d5db' }}></i>
                      <p className="mt-2 mb-1" style={{ fontWeight: '500', color: '#6b7280' }}>No Active Subscription</p>
                      <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                        Choose an annual plan and pay online to activate subscription access for your organization.
                      </p>
                    </div>

                    {annualPackages.length > 0 ? (
                      <div className="row g-3 align-items-stretch">
                        {annualPackages.map(pkg => (
                          <div className="col-md-6 col-xl-4" key={pkg.id}>
                            <div className="border rounded p-3 h-100 d-flex flex-column" style={{ transition: 'box-shadow 0.2s' }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(102,126,234,0.15)'}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                              <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>{pkg.name}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{pkg.description}</div>
                              <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#667eea' }}>
                                {pkg.currency} {parseFloat(pkg.price).toLocaleString()}
                                <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#9ca3af' }}> / {pkg.durationDays} days</span>
                              </div>
                              <ul style={{ fontSize: '0.75rem', color: '#6b7280', paddingLeft: '1rem', marginTop: '0.5rem', marginBottom: '0.75rem' }}>
                                <li>Max voters: {pkg.maxVoters || 'Unlimited'}</li>
                                <li>Max positions: {pkg.maxPositions || 'Unlimited'}</li>
                                {pkg.customBrandingEnabled && <li>Custom branding</li>}
                                {pkg.advancedAnalyticsEnabled && <li>Advanced analytics</li>}
                              </ul>
                              <button
                                className="btn btn-sm w-100 mt-auto"
                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', fontWeight: '600' }}
                                onClick={() => handleSubscribe(pkg.id)}
                                disabled={subscribing}
                              >
                                {subscribing ? (
                                  <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                                ) : (
                                  <><i className="bi bi-credit-card me-1"></i>Subscribe Now</>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center mb-0" style={{ fontSize: '0.8rem' }}>
                        No annual plans are currently available. Contact your VoteWave administrator.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Configuration Card - FOR SUPER ADMINS ONLY */}
          {isSuperAdmin && (
            <>
          <div className="card">
            <div className="card-header bg-white">
              <h5 className="mb-0" style={{ fontWeight: '600' }}>
                <i className="bi bi-gear me-2" style={{ color: '#667eea' }}></i>
                Payment Configuration
              </h5>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <label className="form-label" style={{ fontWeight: '600' }}>
                  Service Charge (UGX)
                </label>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  {settings?.service_charge?.description}
                </p>
                <div className="input-group" style={{ maxWidth: '300px' }}>
                  <span className="input-group-text">UGX</span>
                  <input
                    type="number"
                    className="form-control"
                    value={serviceCharge}
                    onChange={(e) => setServiceCharge(e.target.value)}
                    min="0"
                    step="1"
                  />
                </div>
                <small className="text-muted d-block mt-2">
                  This amount will be added to each vote payment transaction.
                </small>
              </div>

              <div className="alert alert-info d-flex align-items-start" style={{ borderLeft: '4px solid #3b82f6' }}>
                <i className="bi bi-info-circle me-3" style={{ fontSize: '1.25rem', marginTop: '2px' }}></i>
                <div style={{ fontSize: '0.875rem' }}>
                  <strong>Note:</strong> Changes to the service charge will apply immediately to all new payments. 
                  Existing pending transactions will not be affected.
                </div>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setServiceCharge(settings?.service_charge?.value || 300)}
                  disabled={saving}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Additional Settings Section */}
          <div className="card mt-4">
            <div className="card-header bg-white">
              <h5 className="mb-0" style={{ fontWeight: '600' }}>
                <i className="bi bi-info-circle me-2" style={{ color: '#10b981' }}></i>
                About Service Charge
              </h5>
            </div>
            <div className="card-body">
              <p style={{ fontSize: '0.875rem', lineHeight: '1.8', color: '#6b7280' }}>
                The service charge is a fee that VoteWave adds to each paid vote transaction. This charge covers:
              </p>
              <ul style={{ fontSize: '0.875rem', lineHeight: '1.8', color: '#6b7280' }}>
                <li>Payment processing fees</li>
                <li>Platform maintenance and infrastructure</li>
                <li>Security and fraud prevention</li>
                <li>Customer support services</li>
              </ul>
              <p className="mb-0" style={{ fontSize: '0.875rem', lineHeight: '1.8', color: '#6b7280' }}>
                The service charge is clearly communicated to users in the Terms and Conditions and is added 
                transparently to the organization's set vote price during checkout.
              </p>
            </div>
          </div>
            </>
          )}
        </div>

        {/* Sidebar Info - FOR SUPER ADMINS ONLY */}
        {isSuperAdmin && (
          <div className="col-lg-4 mt-4 mt-lg-0">
            <div className="card" style={{ borderLeft: '4px solid #667eea' }}>
              <div className="card-body">
                <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>
                  <i className="bi bi-question-circle me-2"></i>
                  Need Help?
                </h6>
                <p style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#6b7280' }}>
                  If you're unsure about the appropriate service charge amount, consider factors like:
                </p>
                <ul style={{ fontSize: '0.875rem', lineHeight: '1.8', color: '#6b7280', paddingLeft: '1.25rem' }}>
                  <li>Payment gateway fees</li>
                  <li>Operating costs</li>
                  <li>Market standards</li>
                  <li>User affordability</li>
                </ul>
              </div>
            </div>

            <div className="card mt-3" style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className="card-body">
                <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Important
                </h6>
                <p className="mb-0" style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#6b7280' }}>
                  Remember to update the Terms and Conditions if you significantly change the service charge amount 
                  to maintain transparency with your users.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
